import { useState, useEffect, useRef } from "react";
import { logOut } from "../../../config/user";
import { handleApiErrorResponses } from "../../../utils/ApisErrorHandling";
import { AuthServices } from "../../../services/AuthServices";
import { CaseServices } from "../../../services/CaseServices";
import { PreinitiateServices } from "../../../services/PreinitiateServices";
import { ProcessingServices } from "../../../services/ProcessingServices";
import CloseIcon from "../../../assets/hospitalCloseIcon.svg";
import uploadIconGreen from "../../../assets/uploadIcon.svg";
import { CircularProgress } from "@mui/material";
import { pdfjs } from "react-pdf";
import { toast } from "react-toastify";

pdfjs.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;
const UploadAdmissionInitiateRefDoc = ({
  selectedScheme,
  setOpen,
  selectedCase,
  AllCases,
  caseType,
  stayDuration,
  stratification,
  subType,
  admissionDate,
  subTypeOptions,
  surgeryTreatmentStartDate,
}) => {
  const [pdfDocument, setPdfDocument] = useState(null);
  const [images, setImages] = useState([]);
  const [processing, setProcessing] = useState(false);
  const [uploadStatus, setUploadStatus] = useState(false);
  const [caseInitiationStatus, setCaseInitiationStatus] = useState(false);
  const [selectedCount, setSelectedCount] = useState(0);
  const [selectAll, setSelectAll] = useState(true);
  const [percentage, setPercentage] = useState(0);
  const [description, setDescription] = useState("");
  const [tpaUploading, setTpaUploading] = useState(false);
  const [tpaDocuments, setTpaDocuments] = useState([]);
  const [primaryChecks, setPrimaryChecks] = useState(null);
  // Original file names already uploaded in this initiation session (for duplicate check)
  const [uploadedTpaFileNames, setUploadedTpaFileNames] = useState([]);
  // Ref keeps latest docs during async upload so append never loses prior uploads
  const tpaDocumentsRef = useRef([]);
  const fileInputRef = useRef();
  const tpaFileInputRef = useRef();

  const normalizeDocName = (name) =>
    String(name || "")
      .trim()
      .toLowerCase();

  const mergeTpaDocuments = (previousDocs, incomingDocs) => {
    const prev = Array.isArray(previousDocs) ? previousDocs : [];
    const incoming = Array.isArray(incomingDocs) ? incomingDocs : [];
    const existingUrls = new Set(
      prev.map((doc) => String(doc?.url || "")).filter(Boolean),
    );
    const existingNames = new Set(
      prev.map((doc) => normalizeDocName(doc?.name)).filter(Boolean),
    );
    const toAdd = incoming.filter((doc) => {
      const url = String(doc?.url || "");
      const name = normalizeDocName(doc?.name);
      if (url && existingUrls.has(url)) return false;
      // Same classified name + same url already handled; allow same name only if url differs
      if (url) return true;
      if (name && existingNames.has(name)) return false;
      return true;
    });
    return [...prev, ...toAdd];
  };

  const handleButtonClick = () => {
    fileInputRef.current.click();
  };
  const handleTpaButtonClick = () => {
    tpaFileInputRef.current.click();
  };

  async function updateCaseStatus() {
    const subMasterStageData = {
      commandCeneterStage: "Admission Documents Uploaded",
    };
    try {
      const stageResponse = await CaseServices.updateSubMasterStage(
        selectedCase?.id,
        subMasterStageData,
      );
      console.log("Case status update response:", stageResponse?.data);
      toast.success("Case status updated successfully");
      await AllCases();
      setCaseInitiationStatus(false);
      setOpen(false);
    } catch (error) {
      handleApiErrorResponses(error);
    }
  }
  async function mapTpaDocuments() {
    const processingBaseUrl = process.env.REACT_APP_PROCESSING_API_URL;
    // Each classified document returned by the processing API becomes one document
    const classifiedDocuments = (tpaDocuments || []).map((document) => ({
      caseId: selectedCase?.id,
      documentType: "ADMISSIONDOCUMENT",
      documentName: document?.name,
      status: "INREVIEW",
      documenturlmasters: [
        {
          // Encode each path segment so names with spaces or & stay valid in the URL
          documentUrl: `${processingBaseUrl}${String(document?.url || "")}`,
          sequence: 1,
          status: "UPLOADED",
          deleted: false,
        },
      ],
    }));

    // Template slot for CC upload — no documenturlmasters
    const templateDocuments = [
      {
        caseId: selectedCase?.id,
        documentName: "Preauth Template",
        documentType: "ADMISSIONDOCUMENT",
        status: "PENDING",
      },
    ];

    const payload = {
      documents: [...classifiedDocuments, ...templateDocuments],
    };
    try {
      const response = await CaseServices.mapSaveQueryDocuments(payload);
      console.log("TPA documents map response:", response?.data);
      console.log("payload", payload);
      toast.success("TPA documents mapped successfully");
      await updateCaseStatus();
    } catch (error) {
      handleApiErrorResponses(error);
    }
  }
  async function uploadTpaDocument(event) {
    const selectedFiles = Array.from(event.target.files || []);
    // Reset input so selecting the same files again still triggers onChange
    event.target.value = "";

    if (selectedFiles.length === 0) return;

    const allowedExtensions = [".pdf", ".png", ".jpg", ".jpeg"];
    const validFiles = selectedFiles.filter((file) =>
      allowedExtensions.some((ext) => file.name.toLowerCase().endsWith(ext)),
    );
    if (validFiles.length !== selectedFiles.length) {
      toast.error("Only PDF, PNG, JPG and JPEG files are allowed");
    }
    if (validFiles.length === 0) return;

    // Block re-upload of the same file already uploaded in this session
    const alreadyUploaded = new Set([
      ...uploadedTpaFileNames.map(normalizeDocName),
      ...(tpaDocuments || []).map((doc) => normalizeDocName(doc?.name)),
    ]);
    const seenInBatch = new Set();
    const duplicateFiles = [];
    const newFiles = [];

    validFiles.forEach((file) => {
      const key = normalizeDocName(file.name);
      if (alreadyUploaded.has(key) || seenInBatch.has(key)) {
        duplicateFiles.push(file);
      } else {
        seenInBatch.add(key);
        newFiles.push(file);
      }
    });

    if (duplicateFiles.length > 0) {
      toast.error("This document has already been uploaded.");
    }
    if (newFiles.length === 0) return;

    setTpaUploading(true);
    try {
      const formData = new FormData();
      newFiles.forEach((file) => formData.append("files", file));

      const response =
        await ProcessingServices.uploadAdmissionInitiateRefDoc(formData);
      console.log("TPA documents upload response:", response?.data);
      setPrimaryChecks(response?.data?.primary_check?.checks);
      // Store the classified documents ([{ name, key, url }]) for mapTpaDocuments
      const uploadedDocuments = Array.isArray(response?.data)
        ? response.data
        : response?.data?.files || response?.data?.documents || [];
      const filteredDocuments = uploadedDocuments.filter(
        (document) => !document?.name?.includes("manifest"),
      );

      setUploadedTpaFileNames((prev) => [
        ...prev,
        ...newFiles.map((file) => normalizeDocName(file.name)),
      ]);
      // Always append to previously uploaded docs (never replace the list)
      const mergedDocuments = mergeTpaDocuments(
        tpaDocumentsRef.current,
        filteredDocuments,
      );
      tpaDocumentsRef.current = mergedDocuments;
      setTpaDocuments(mergedDocuments);
      toast.success(
        `${newFiles.length} document${newFiles.length > 1 ? "s" : ""} uploaded successfully`,
      );
      // return response?.data;
    } catch (error) {
      handleApiErrorResponses(error);
    } finally {
      setTpaUploading(false);
    }
  }
  const handleInitiateAdmission = async () => {
    setCaseInitiationStatus(true);
    const data = {
      caseType: caseType,
      admissionDateByHospital: admissionDate || null,
      lengthOfStay: caseType === "CONSERVATIVE" ? stayDuration : "",
      wardByHospital:
        selectedScheme.value === "TPA"
          ? stratification
          : caseType === "CONSERVATIVE"
            ? stratification
            : null,
      surgeryTreatmentStartDate: surgeryTreatmentStartDate,
    };
    console.log("data:", data);
    try {
      await CaseServices.initiateAdmission(selectedCase?.id, data);
      toast.success("Admission Initiated");
      if (selectedScheme.value === "TPA") {
        await mapTpaDocuments();
      } else {
        await AllCases();
        setOpen(false);
        setCaseInitiationStatus(false);
      }
    } catch (error) {
      handleApiErrorResponses(error);
    }
  };
  //Method to upload the pdf doc
  async function uploadDocument(event) {
    setProcessing(true);
    try {
      const file = event.target.files[0];
      const fileType = file.type.split("/")[0];
      if (
        fileType === "application" &&
        file.name.toLowerCase().endsWith(".pdf")
      ) {
        setDescription(event.target.files[0].name.slice(0, -4));
        const uri = URL.createObjectURL(file);
        const _PDF_DOC = await pdfjs.getDocument(uri).promise;
        setPdfDocument(_PDF_DOC);
      } else if (fileType === "image") {
        const imageBlob = URL.createObjectURL(file);
        setImages([...images, { isSelected: true, documentUrl: imageBlob }]);
        setSelectedCount((prev) => prev + 1);
        setProcessing(false);
      }
    } catch (error) {
      handleApiErrorResponses(error);
    }
  }
  //Method to get the image list from the uploaded pdf document
  async function getImageList() {
    const imagesList = [...images];

    try {
      for (let i = 1; i <= pdfDocument.numPages; i++) {
        const canvas = document.createElement("canvas");
        canvas.setAttribute("class", "canv");
        const page = await pdfDocument.getPage(i);
        const viewport = page.getViewport({ scale: 3 });
        canvas.height = viewport.height;
        canvas.width = viewport.width;
        const renderContext = {
          canvasContext: canvas.getContext("2d"),
          viewport: viewport,
        };

        await page.render(renderContext).promise;
        await new Promise((resolve) => {
          canvas.toBlob((blob) => {
            if (blob) {
              const blobUrl = URL.createObjectURL(blob);
              imagesList.push({ isSelected: true, documentUrl: blobUrl });
              setSelectedCount((prev) => prev + 1);
              if (i === pdfDocument.numPages) {
                setImages([...imagesList]);
              }
            }
            resolve();
          }, "image/jpeg");
        });
      }
    } catch (error) {
      handleApiErrorResponses(error);
    }
    setSelectedCount(imagesList.length);
    setProcessing(false);
  }
  //This method  to save the newly uploaded document's url to settlemed database
  const addDocument = async (docUrl) => {
    if (docUrl) {
      const sequence = 1; //here we are generating sequence for the newly added document
      const modifiedData = docUrl?.map((url, idx) => {
        return {
          parentId: `${description?.slice(0, 4)}${new Date().getTime()}${
            1 + docUrl.length
          }${docUrl.length}`,
          caseId: selectedCase.id,
          documentUrl: url,
          sequence: sequence + idx,
          description: `${description}_${idx}`,
          documentType: "ADMISSIONDOCUMENT",
        };
      });
      try {
        await PreinitiateServices.addReferenceDocuments(modifiedData);
        toast.success("Documents Added Successfully ");

        await handleInitiateAdmission();
      } catch (error) {
        handleApiErrorResponses(error);
      }
      setDescription(null);
    }
    setPercentage(0);
    setUploadStatus(false);
  };
  //Method to upload the image list's blob data to s3 bucket
  const uploadImages = async () => {
    if (selectedCount > 0) {
      setUploadStatus(true);
      let data = new FormData();

      for (let index = 0; index < images.length; index++) {
        if (images[index].isSelected) {
          let image = images[index].documentUrl;
          if (!(image instanceof Blob)) {
            const response = await fetch(image);
            image = await response.blob();
          }
          data.append("images", image, `${description + "_" + index}.jpeg`);
        }
      }
      try {
        let res = await AuthServices.uploadPdfToImage(data, setPercentage);
        console.log("Image data resonse from apis", res.data.urls);
        //   setDocUrl(res?.data?.urls);
        await addDocument(res?.data?.urls);
        setPdfDocument(null);
        setImages([]);
        // window.alert("Image uploaded successfully");
      } catch (error) {
        handleApiErrorResponses(error);
      }
    } else {
      window.alert("Please Select Atleast One Image");
    }
  };

  const handleSelection = (index) => {
    const modifiedData = images.map((document, idx) => {
      if (idx === index) {
        if (document.isSelected) {
          setSelectedCount(selectedCount - 1);
        } else {
          setSelectedCount(selectedCount + 1);
        }
        return { ...document, isSelected: !document.isSelected };
      } else {
        return document;
      }
    });
    // setDocumentsUrl(modifiedData);
    setImages(modifiedData);
  };
  const handleSelectAll = () => {
    if (selectAll) {
      const modifiedData = images.map((document) => {
        return { ...document, isSelected: false };
      });
      setSelectedCount(0);
      setImages([...modifiedData]);
    } else {
      const modifiedData = images.map((document) => {
        return { ...document, isSelected: true };
      });
      setImages([...modifiedData]);
      setSelectedCount(images.length);
    }
  };
  useEffect(() => {
    //This is done to check selectAll check box when all documents are selected
    if (selectedCount && selectedCount === images?.length) {
      setSelectAll(true);
    } else {
      setSelectAll(false);
    }
  }, [selectedCount]);
  useEffect(() => {
    pdfDocument && getImageList();
  }, [pdfDocument]);

  return (
    <div className="fixed top-0 left-0 h-screen w-screen flex justify-center items-center bg-[#017C7180] z-20">
      <div className="relative h-[80%] w-[90%] 2xl:w-[80%] bg-white rounded-[30px] p-10">
        <div
          className="absolute top-3 right-4 cursor-pointer"
          onClick={() => setOpen(false)}
        >
          <img src={CloseIcon} alt="close-icon" height={53} width={52} />
        </div>
        <div className="flex justify-between items-end mr-8">
          <div className="text-[24px] 2xl:text-[30px]">
            {tpaDocuments?.length > 0
              ? "Classified Reference Documents"
              : "Upload Document"}
          </div>
          {images?.length > 0 && (
            <div>
              <label htmlFor="selectAll">Select All</label>{" "}
              <input
                type="checkbox"
                id="selectAll"
                checked={selectAll}
                className="h-[16px] w-[16px]"
                onClick={handleSelectAll}
              />
            </div>
          )}
        </div>

        <div
          style={{ border: "1px solid #E4E4E4" }}
          className="w-[100%] h-[80%] flex justify-center items-start  gap-2 flex-wrap overflow-y-scroll mt-4 p-1  box-border rounded-[20px]"
        >
          {" "}
          {tpaDocuments?.length > 0 ? (
            <div className="relative w-full min-h-full">
              {tpaUploading && (
                <div className="absolute inset-0 z-10 flex flex-col items-center justify-center gap-3 rounded-[20px] bg-[#FFFFFFCC]">
                  <CircularProgress />
                  <div className="text-sm font-medium text-[#017C71]">
                    Processing document… it will appear shortly
                  </div>
                </div>
              )}
              <div className="w-full flex flex-wrap gap-4">
                {tpaDocuments.map((document, idx) => {
                  const fileUrl = document?.url
                    ? `${process.env.REACT_APP_PROCESSING_API_URL}${document.url
                        .split("/")
                        .map(encodeURIComponent)
                        .join("/")}`
                    : "";
                  const fileName = document?.name || "";
                  const isPdf = /\.pdf$/i.test(fileName);
                  const isImage = /\.(jpe?g|png)$/i.test(fileName);
                  // "OPD_OPD_Notes.pdf" -> "OPD OPD Notes"
                  const label = fileName
                    .replace(/\.[^.]+$/, "")
                    .replace(/_+/g, " ")
                    .trim();

                  return (
                    <a
                      key={`${fileName}-${document?.url || idx}`}
                      href={fileUrl || "#"}
                      target="_blank"
                      rel="noopener noreferrer"
                      title={fileName}
                      style={{ border: "1px solid #E4E4E4" }}
                      className="relative block w-[256px] h-[290px] box-border bg-white rounded-[17px] overflow-hidden no-underline cursor-pointer"
                    >
                      <div className="absolute top-3 left-4 z-[2] text-[24px] leading-[100%] text-[#000000]">
                        {idx + 1}.
                      </div>

                      {/* Document preview */}
                      <div className="absolute inset-0 bg-white">
                        {fileUrl && isPdf ? (
                          <iframe
                            src={`${fileUrl}#toolbar=0&navpanes=0&scrollbar=0&view=FitH`}
                            title={fileName}
                            className="w-full h-full border-0 pointer-events-none"
                          />
                        ) : fileUrl && isImage ? (
                          <img
                            src={fileUrl}
                            alt={fileName}
                            className="w-full h-full object-cover object-top"
                          />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center text-[14px] text-[#969696]">
                            Preview not available
                          </div>
                        )}
                      </div>

                      {/* Document name pill */}
                      <div className="absolute bottom-3 left-3 right-3 z-[2] h-[40px] px-4 flex items-center justify-center rounded-full bg-[#80808099] backdrop-blur-[4px]">
                        <span className="text-[16px] font-medium text-[#FFFFFF] truncate">
                          {label}
                        </span>
                      </div>
                    </a>
                  );
                })}
              </div>
            </div>
          ) : images.length > 0 ? (
            images.map((image, idx) => {
              return (
                <div
                  key={idx}
                  style={{ border: "1px solid #E4E4E4" }}
                  className="relative bg-white rounded-[17px] p-1"
                >
                  <div className="flex justify-between px-2">
                    <div>{idx + 1}</div>
                    <div>
                      <input
                        type="checkbox"
                        checked={image.isSelected}
                        onClick={() => handleSelection(idx)}
                        //   className="h-[20px] w-[20px]"
                      />
                    </div>
                  </div>
                  <img
                    src={image?.documentUrl}
                    alt=""
                    width={248}
                    height={249}
                    style={{ border: "1px solid #E4E4E4" }}
                    className="object-fit rounded-[15px]"
                  />
                  <div className="absolute bottom-0.5 left-0  w-full ">
                    <div
                      style={{
                        background: "#00000060",
                        backdropFilter: "blur(3.6976048946380615px)",
                      }}
                      className="flex w-[94%] justify-between items-center rounded-[14px] py-2 mx-auto px-1"
                    >
                      {/* <div className="text-sm text-white leading-[18.27px] -tracking-[3%] text-ellipsis whitespace-nowrap overflow-hidden">
                      {document.description}
                    </div> */}
                      <div className="flex items-center gap-4">
                        {/* <div
                        className="cursor-pointer"
                        onClick={() => deleteDocument(document.id)}
                      >
                        <img src={HospitalDelIcon} alt="" />
                      </div> */}
                        {/* <div>
                        {" "}
                        <img
                          src={RightArrow}
                          alt="arrow-icon"
                          height={12}
                          width={12}
                          className="-rotate-45 cursor-pointer"
                        />
                      </div> */}
                      </div>
                    </div>
                  </div>
                </div>
              );
            })
          ) : (
            <div className="h-full w-full flex justify-center items-center">
              {processing || tpaUploading ? (
                <CircularProgress />
              ) : (
                <div className="flex items-center gap-3">
                  {selectedScheme.value !== "TPA" ? (
                    <div
                      style={{ border: "1px solid #017C71" }}
                      className="flex items-center gap-3 py-3 px-5 text-[#017C71] rounded-full cursor-pointer"
                      onClick={handleButtonClick}
                    >
                      Upload Files{" "}
                      <img src={uploadIconGreen} alt="upload-icon" />
                    </div>
                  ) : (
                    <div
                      style={{ border: "1px solid #017C71" }}
                      className="flex items-center gap-3 py-3 px-5 text-[#017C71] rounded-full cursor-pointer"
                      onClick={handleTpaButtonClick}
                    >
                      Upload TPA Documents{" "}
                      <img src={uploadIconGreen} alt="upload-icon" />
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
        <input
          type="file"
          onChange={(e) => uploadDocument(e)}
          style={{ display: "none" }}
          ref={fileInputRef}
          accept=".pdf,.png,.jpg,.jpeg"
        />
        <input
          type="file"
          multiple
          onChange={uploadTpaDocument}
          style={{ display: "none" }}
          ref={tpaFileInputRef}
          accept=".pdf,.png,.jpg,.jpeg"
        />
        {uploadStatus || processing || tpaUploading ? (
          <div className="flex justify-end items-center gap-2 mt-1">
            <CircularProgress size={22} sx={{ color: "#017C71" }} />
            {tpaUploading && (
              <span className="text-xs 2xl:text-sm text-[#017C71]">
                Processing document…
              </span>
            )}
          </div>
        ) : (
          (images?.length > 0 || tpaDocuments?.length > 0) && (
            <div>
              <div className="flex justify-end items-center gap-3 text-xs 2xl:text-sm text-white -tracking-[3%] uppercase mt-1">
                <div
                  className="py-2 2xl:py-3 px-4 2xl:px-5  text-[#017C71] cursor-pointer underline underline-offset-3"
                  onClick={
                    selectedScheme.value !== "TPA"
                      ? handleButtonClick
                      : handleTpaButtonClick
                  }
                >
                  Upload New Document
                </div>

                {selectedScheme.value !== "TPA" && (
                  <div
                    className="py-2 2xl:py-3 px-7 2xl:px-8 bg-[#017C71] rounded-full cursor-pointer"
                    onClick={uploadImages}
                  >
                    Save
                  </div>
                )}
              </div>
              {selectedScheme.value === "TPA" && (
                <div className="w-full flex items-center justify-center ">
                  {caseInitiationStatus ? (
                    <CircularProgress />
                  ) : (
                    <div
                      className="text-sm -tracking-[0.03em] py-2 2xl:py-3 px-7 2xl:px-8 bg-[#017C71] text-white rounded-full cursor-pointer"
                      onClick={() => {
                        if (primaryChecks && primaryChecks[0]?.passed) {
                          handleInitiateAdmission();
                        } else {
                          toast.error(
                            primaryChecks?.[0]?.reason || "No OPD page found",
                          );
                        }
                      }}
                    >
                      Initiate Admission
                    </div>
                  )}
                </div>
              )}
            </div>
          )
        )}
      </div>
    </div>
  );
};

export default UploadAdmissionInitiateRefDoc;
